import React from 'react';
import styled, { createGlobalStyle, css } from 'styled-components';
import { TagError } from '../../Input/styles';

interface SelectedProps {
  selected?: boolean;
  haveChildren?: boolean;
  resize?: boolean;
  error?: boolean;
}

const GlobalStyle = createGlobalStyle`
  .selected {
    border-color: ${(props) => props.theme.colors.primary};
    border-style: solid;
    border-width: 1px;
  }
`;

const Wrapper = styled.div<SelectedProps>`
  display: flex;
  justify-content: flex-start;
  font-size: 15px;
  height: ${({ resize }) => (resize ? '40px' : '76px')};
  box-shadow: -4px -4px 8px rgba(36, 36, 36, 0.1),
    4px 4px 6px rgba(36, 36, 36, 0.1);
  border: ${(props) =>
    `2px solid ${
      props.selected ? props.theme.colors.primaryDark1 : '2px solid transparent'
    }`};
  border-radius: ${(props) =>
    Number(props.theme.radiusCards) ? `${props.theme.radiusCards}px` : '60px'};

  margin: 12px 0;
  background: #fff;
  padding: ${({ haveChildren }) => (haveChildren ? '0px' : '15px 0px')};

  ${({ haveChildren }) =>
    haveChildren &&
    css`
      position: relative;
      min-height: 40px;
    `}

  ${(props) =>
    props.error &&
    css`
      border: 2px solid ${props.theme.colors.danger};
    `}

  &:hover {
    cursor: pointer;
  }

  @media screen and (max-width: 960px) {
    border-radius: ${(props) =>
      Number(props.theme.radiusCards)
        ? `${props.theme.radiusCards}px`
        : '10px'};
    width: 100%;
    font-size: 14px;
    height: ${({ resize }) => (resize ? '40px' : '90px')};
  }
`;

const AddressContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  margin: 0 32px;
  @media screen and (max-width: 960px) {
    justify-content: space-between;
    margin: 0 16px;
  }

  @media screen and (max-width: 600px) {
    justify-content: space-between;
    width: 100%;
  }
`;

const Section = styled.div`
  display: flex;
  align-items: center;
`;

const ContentCardTitle = styled.div<{ hasTitle?: boolean }>`
  font-family: ${(props) => props.theme.primaryFont};
  font-style: normal;
  font-weight: ${(props) => (props.hasTitle ? 'bold ' : 'normal')};
  font-size: 14px;
  line-height: 14px;
  max-width: 244px;
  margin-bottom: 8px;
  color: #222222;

  @media (min-width: 460px) {
    max-width: 440px;
  }
`;

const ContentCard = styled.span<{ hasTitle?: boolean }>`
  font-family: ${(props) => props.theme.primaryFont};
  font-style: normal;
  font-weight: ${(props) => (props.hasTitle ? 'normal ' : 'bold')};
  font-size: 14px;
  line-height: 14px;
  color: ${(props) => props.theme.colors.black};
`;

const Texts = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;

  @media screen and (max-width: 960px) {
    width: auto;
    margin: 0 0 0 12px;
  }
`;

const SelectContainer = styled.div<SelectedProps>`
  display: flex;
  align-items: center;
  justify-content: center;
  border: ${(props) =>
    `2px solid ${props.selected ? props.theme.colors.primaryDark1 : '#000'}`};
  min-height: 24px;
  min-width: 24px;
  border-radius: 50%;
  align-self: center;
  cursor: pointer;

  ${(props) =>
    props.error &&
    css`
      border: 2px solid ${props.theme.colors.danger};
    `}

  span {
    background: ${(props) =>
      props.selected ? props.theme.colors.primaryDark1 : 'none'};
    align-self: center;
    justify-self: center;
    height: 14px;
    width: 14px;
    border-radius: 50%;

    ${(props) =>
      props.error &&
      css`
        background: ${props.theme.colors.danger};
      `}
  }
`;

interface CardProps {
  selectedId?: string | number | null;
  setOption?: React.Dispatch<React.SetStateAction<string | number | null>>;
  id?: string | number | null;
  contentCardTitleText?: string;
  contentCardText?: string;
  children?: React.ReactNode;
  haveChildren?: boolean;
  resize?: boolean;
  error?: boolean;
  errorMessage?: string;
  newContact?: boolean;
  setNew?: React.Dispatch<React.SetStateAction<boolean>>;
  setError?: React.Dispatch<React.SetStateAction<boolean>>;
  idToSelect?: string | number | null;
  hideSelector?: boolean;
  onClickCard?: () => void;
  setStep?: React.Dispatch<React.SetStateAction<number>>;
}

const ConfirmationCard: React.FC<CardProps> = ({
  selectedId,
  setOption,
  id,
  contentCardText,
  contentCardTitleText,
  children,
  haveChildren,
  resize,
  error,
  errorMessage,
  newContact,
  setNew,
  setError,
  idToSelect,
  hideSelector,
  onClickCard,
  setStep,
}) => {
  const handleClick = () => {
    if (onClickCard) {
      onClickCard();
      return;
    }
    setOption && setOption(idToSelect ?? (id || null));
    if (children && setNew) {
      setNew(true);
    } else if (!children && setNew) {
      setNew(false);
      if (error && setError) {
        setError(false);
      }
    }
  };

  if (!children) {
    return (
      <Wrapper selected={selectedId === (idToSelect || id)} resize={resize}>
        <GlobalStyle />
        <AddressContainer onClick={handleClick}>
          <Section>
            <Texts>
              {contentCardTitleText && (
                <ContentCardTitle hasTitle={!!contentCardTitleText}>
                  {contentCardTitleText}
                </ContentCardTitle>
              )}
              <ContentCard hasTitle={!!contentCardTitleText}>
                {contentCardText}
              </ContentCard>
            </Texts>
          </Section>

          {!hideSelector && (
            <SelectContainer selected={selectedId === (idToSelect || id)}>
              <span />
            </SelectContainer>
          )}
        </AddressContainer>
      </Wrapper>
    );
  } else {
    return (
      <Wrapper
        selected={selectedId === (idToSelect || id)}
        haveChildren={haveChildren}
        resize={resize}
        error={error}
      >
        <GlobalStyle />
        <AddressContainer onClick={handleClick}>
          <Section>
            <Texts>
              {contentCardTitleText && (
                <ContentCardTitle hasTitle={!!contentCardTitleText}>
                  {contentCardTitleText}
                </ContentCardTitle>
              )}
              <ContentCard hasTitle={!!contentCardTitleText}>
                {children}
              </ContentCard>
            </Texts>
          </Section>

          <SelectContainer
            selected={selectedId === (idToSelect || id)}
            error={error}
          >
            <span />
          </SelectContainer>
        </AddressContainer>
        {error && newContact && (
          <TagError haveChildren={haveChildren}>{errorMessage}</TagError>
        )}
      </Wrapper>
    );
  }
};

export default ConfirmationCard;
